/**
 * Generic multi-select combobox with chips, search, and custom rendering.
 *
 * Substitui o Custom Element legado `<multiselect-input>` com uma
 * implementacao acessivel baseada em Radix Popover (foco/teclado/click-outside)
 * + lista de opcoes navegavel por teclado.
 *
 * Atende RF44 (TASK-020).
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import * as RP from '@radix-ui/react-popover';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@favelafood/shared/lib/cn';

const DEFAULT_MAX_DROPDOWN_HEIGHT = 320;

export interface MultiSelectProps<T> {
  /** Lista completa de opcoes disponiveis. */
  options: ReadonlyArray<T>;
  /** Selecao atual (controlled). */
  value: ReadonlyArray<T>;
  /** Callback ao alterar a selecao. */
  onChange: (selected: T[]) => void;
  /** Extrai chave estavel da opcao (usada para `key` e dedupe). */
  getOptionKey: (option: T) => string;
  /** Texto exibido em chip e item da lista. */
  getOptionLabel: (option: T) => string;
  /** Habilita o campo de busca dentro do popover (default: true). */
  searchable?: boolean;
  /**
   * Filtro custom aplicado a `options` quando o usuario digita na busca.
   * Default: `getOptionLabel().toLowerCase().includes(query.toLowerCase())`.
   */
  filterFn?: (option: T, query: string) => boolean;
  /** Render custom para cada item do dropdown (sobrescreve label visual). */
  renderOption?: (option: T) => ReactNode;
  /** Render custom para o chip do item selecionado. */
  renderChip?: (option: T, onRemove: () => void) => ReactNode;
  /** Texto exibido quando nada esta selecionado. */
  placeholder?: string;
  /** Quando `true`, desabilita interacoes. */
  disabled?: boolean;
  /** Mensagem quando o dropdown esta vazio (sem matches). */
  emptyMessage?: string;
  /** Altura maxima do dropdown em pixels (default: 320). */
  maxHeight?: number;
  /** Classes extra aplicadas ao wrapper externo. */
  className?: string;
  /** Aria-label para o campo (acessibilidade quando nao ha label visivel). */
  ariaLabel?: string;
  /**
   * Callback opcional disparado quando o termo de busca muda. Util para
   * especializacoes que precisam disparar fetches assincronos (ex.
   * `<UserMultiSelect>`).
   */
  onSearchChange?: (query: string) => void;
}

/** Indice nao destacado. Constante deixa o intent explicito no codigo. */
const NO_INDEX = -1;

/** Default filter: case-insensitive substring match no label. */
function defaultFilterFn<T>(getOptionLabel: (o: T) => string) {
  return (option: T, query: string): boolean =>
    getOptionLabel(option).toLowerCase().includes(query.toLowerCase());
}

/** Aplica filtro de busca; retorna a lista original quando query vazia. */
function applyFilter<T>(
  options: ReadonlyArray<T>,
  query: string,
  filter: (o: T, q: string) => boolean,
): T[] {
  if (query.trim() === '') return [...options];
  return options.filter((opt) => filter(opt, query));
}

/** Avanca o highlight respeitando os limites da lista. */
function nextHighlight(current: number, length: number, delta: number): number {
  if (length === 0) return NO_INDEX;
  if (current === NO_INDEX) return delta > 0 ? 0 : length - 1;
  const next = current + delta;
  if (next < 0) return length - 1;
  if (next >= length) return 0;
  return next;
}

/** Toggle de selecao baseado em chave; retorna novo array. */
function toggleSelection<T>(
  current: ReadonlyArray<T>,
  option: T,
  getOptionKey: (o: T) => string,
): T[] {
  const key = getOptionKey(option);
  const exists = current.some((it) => getOptionKey(it) === key);
  if (exists) {
    return current.filter((it) => getOptionKey(it) !== key);
  }
  return [...current, option];
}

/**
 * Trigger visual — emula o estilo do `<Input>` para consistencia.
 * Renderizado dentro de `Popover.Trigger` via `asChild`.
 */
interface TriggerBoxProps<T> {
  value: ReadonlyArray<T>;
  placeholder: string;
  disabled: boolean;
  open: boolean;
  ariaLabel: string | undefined;
  ariaControls: string;
  getOptionKey: (o: T) => string;
  getOptionLabel: (o: T) => string;
  renderChip: ((o: T, onRemove: () => void) => ReactNode) | undefined;
  onRemove: (option: T) => void;
}

const TriggerBox = forwardRef(function TriggerBox<T>(
  {
    value,
    placeholder,
    disabled,
    open,
    ariaLabel,
    ariaControls,
    getOptionKey,
    getOptionLabel,
    renderChip,
    onRemove,
    ...rest
  }: TriggerBoxProps<T>,
  ref: React.Ref<HTMLButtonElement>,
): ReactElement {
  const hasSelection = value.length > 0;
  return (
    <button
      ref={ref}
      type="button"
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-controls={ariaControls}
      aria-label={ariaLabel}
      disabled={disabled}
      className={cn(
        'flex min-h-10 w-full items-center gap-1.5 rounded-card border border-border ' +
          'bg-bg-input px-2 py-1.5 text-left text-sm text-white transition-colors ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
          'focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
          'disabled:cursor-not-allowed disabled:opacity-60',
      )}
      {...rest}
    >
      <div className="flex flex-1 flex-wrap items-center gap-1">
        {hasSelection ? (
          value.map((option) => {
            const key = getOptionKey(option);
            const handleRemove = (): void => onRemove(option);
            if (renderChip) {
              return <span key={key}>{renderChip(option, handleRemove)}</span>;
            }
            return (
              <DefaultChip
                key={key}
                label={getOptionLabel(option)}
                onRemove={handleRemove}
                disabled={disabled}
              />
            );
          })
        ) : (
          <span className="px-1 text-muted">{placeholder}</span>
        )}
      </div>
      <ChevronDown
        size={16}
        className={cn('shrink-0 text-muted transition-transform', open && 'rotate-180')}
        aria-hidden
      />
    </button>
  );
}) as <T>(p: TriggerBoxProps<T> & { ref?: React.Ref<HTMLButtonElement> }) => ReactElement;

interface DefaultChipProps {
  label: string;
  onRemove: () => void;
  disabled: boolean;
}

function DefaultChip({ label, onRemove, disabled }: DefaultChipProps): ReactElement {
  const handleClick = (e: React.MouseEvent<HTMLSpanElement>): void => {
    e.stopPropagation();
    if (!disabled) onRemove();
  };
  const handleKey = (e: KeyboardEvent<HTMLSpanElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) onRemove();
    }
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-border bg-bg-hover ' +
          'px-2 py-0.5 text-xs text-white',
      )}
    >
      <span className="max-w-[14rem] truncate">{label}</span>
      <span
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={handleKey}
        aria-label={`Remover ${label}`}
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded-full text-muted ' +
            'transition-colors hover:bg-danger/20 hover:text-danger ' +
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <X size={12} aria-hidden />
      </span>
    </span>
  );
}

interface OptionRowProps<T> {
  option: T;
  index: number;
  listboxId: string;
  isSelected: boolean;
  isHighlighted: boolean;
  onHighlight: (index: number) => void;
  onToggle: (option: T) => void;
  renderOption: ((option: T) => ReactNode) | undefined;
  getOptionLabel: (option: T) => string;
}

/**
 * Linha individual da listbox. Renderiza um `<button>` interno (ja
 * keyboard-acessivel) dentro de um `<li role="option">` para satisfazer
 * a a11y (ENTER/Space sao tratados nativamente pelo button).
 */
function OptionRow<T>({
  option,
  index,
  listboxId,
  isSelected,
  isHighlighted,
  onHighlight,
  onToggle,
  renderOption,
  getOptionLabel,
}: OptionRowProps<T>): ReactElement {
  return (
    <li
      id={`${listboxId}-opt-${index}`}
      data-index={index}
      role="option"
      aria-selected={isSelected}
      className="list-none"
    >
      <button
        type="button"
        tabIndex={-1}
        onMouseEnter={() => onHighlight(index)}
        onClick={() => onToggle(option)}
        className={cn(
          'flex w-full cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 ' +
            'text-left text-sm text-white outline-none transition-colors',
          isHighlighted && 'bg-bg-hover',
          isSelected && 'text-primary',
        )}
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
          {isSelected ? <Check size={14} aria-hidden /> : null}
        </span>
        <span className="flex-1 truncate">
          {renderOption ? renderOption(option) : getOptionLabel(option)}
        </span>
      </button>
    </li>
  );
}

/**
 * `<MultiSelect>` generico.
 *
 * Combobox acessivel com:
 * - Chips inline com botao de remover
 * - Popover com busca opcional
 * - Navegacao completa por teclado (setas, enter, esc, backspace)
 * - Item selecionado marcado com checkmark (a lista nao filtra selecionados)
 */
export function MultiSelect<T>(props: MultiSelectProps<T>): ReactElement {
  const {
    options,
    value,
    onChange,
    getOptionKey,
    getOptionLabel,
    searchable = true,
    filterFn,
    renderOption,
    renderChip,
    placeholder = 'Selecionar...',
    disabled = false,
    emptyMessage = 'Nenhuma opção encontrada',
    maxHeight = DEFAULT_MAX_DROPDOWN_HEIGHT,
    className,
    ariaLabel,
    onSearchChange,
  } = props;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(NO_INDEX);
  const listRef = useRef<HTMLUListElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listboxId = useId();

  const filter = useMemo(() => filterFn ?? defaultFilterFn(getOptionLabel), [filterFn, getOptionLabel]);
  const filtered = useMemo(() => applyFilter(options, query, filter), [options, query, filter]);

  const selectedKeys = useMemo(() => {
    const set = new Set<string>();
    for (const v of value) set.add(getOptionKey(v));
    return set;
  }, [value, getOptionKey]);

  // Reset state quando o popover fecha
  useEffect(() => {
    if (!open) {
      setQuery('');
      setHighlight(NO_INDEX);
    }
  }, [open]);

  // Notifica subscribers (ex. UserMultiSelect) sobre mudancas de busca
  useEffect(() => {
    onSearchChange?.(query);
  }, [query, onSearchChange]);

  // Reset highlight quando a lista filtrada muda
  useEffect(() => {
    setHighlight((prev) => (prev >= filtered.length ? NO_INDEX : prev));
  }, [filtered.length]);

  // Scroll do item destacado para a viewport
  useEffect(() => {
    if (highlight === NO_INDEX || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-index="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const handleToggle = useCallback(
    (option: T) => {
      if (disabled) return;
      onChange(toggleSelection(value, option, getOptionKey));
    },
    [disabled, onChange, value, getOptionKey],
  );

  const handleRemove = useCallback(
    (option: T) => {
      if (disabled) return;
      onChange(value.filter((v) => getOptionKey(v) !== getOptionKey(option)));
    },
    [disabled, onChange, value, getOptionKey],
  );

  const focusTrigger = useCallback(() => {
    triggerRef.current?.focus();
  }, []);

  const handleSearchKey = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlight((prev) => nextHighlight(prev, filtered.length, 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlight((prev) => nextHighlight(prev, filtered.length, -1));
          break;
        case 'Enter': {
          e.preventDefault();
          const target = highlight === NO_INDEX ? null : filtered[highlight];
          if (target !== undefined && target !== null) {
            handleToggle(target);
          }
          break;
        }
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          focusTrigger();
          break;
        case 'Backspace':
          if (query === '' && value.length > 0) {
            e.preventDefault();
            const last = value[value.length - 1];
            if (last !== undefined) handleRemove(last);
          }
          break;
        default:
          break;
      }
    },
    [filtered, highlight, query, value, handleToggle, handleRemove, focusTrigger],
  );

  const handleListKey = useCallback(
    (e: KeyboardEvent<HTMLUListElement>) => {
      // Quando nao ha busca, capturamos teclado direto na lista
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((prev) => nextHighlight(prev, filtered.length, 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((prev) => nextHighlight(prev, filtered.length, -1));
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const target = highlight === NO_INDEX ? null : filtered[highlight];
        if (target !== undefined && target !== null) handleToggle(target);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        focusTrigger();
      }
    },
    [filtered, highlight, handleToggle, focusTrigger],
  );

  return (
    <div className={cn('relative w-full', className)}>
      <RP.Root open={open} onOpenChange={setOpen}>
        <RP.Trigger asChild>
          <TriggerBox
            ref={triggerRef}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            open={open}
            ariaLabel={ariaLabel}
            ariaControls={listboxId}
            getOptionKey={getOptionKey}
            getOptionLabel={getOptionLabel}
            renderChip={renderChip}
            onRemove={handleRemove}
          />
        </RP.Trigger>
        <RP.Portal>
          <RP.Content
            align="start"
            sideOffset={4}
            className={cn(
              'z-50 w-[--radix-popover-trigger-width] overflow-hidden rounded-card ' +
                'border border-border bg-bg-card shadow-2xl animate-fade-in',
            )}
            onOpenAutoFocus={(e) => {
              if (searchable) {
                e.preventDefault();
                searchRef.current?.focus();
              }
            }}
          >
            {searchable ? (
              <div className="border-b border-border p-2">
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleSearchKey}
                  placeholder="Buscar..."
                  aria-label="Buscar opções"
                  aria-controls={listboxId}
                  aria-activedescendant={
                    highlight === NO_INDEX ? undefined : `${listboxId}-opt-${highlight}`
                  }
                  className={cn(
                    'h-9 w-full rounded-md border border-border bg-bg-input px-3 text-sm ' +
                      'text-white placeholder:text-muted outline-none ' +
                      'focus-visible:ring-2 focus-visible:ring-primary',
                  )}
                />
              </div>
            ) : null}
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-multiselectable
              tabIndex={searchable ? -1 : 0}
              onKeyDown={searchable ? undefined : handleListKey}
              className="overflow-y-auto p-1 outline-none"
              style={{ maxHeight }}
            >
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-sm text-muted" role="presentation">
                  {emptyMessage}
                </li>
              ) : (
                filtered.map((option, index) => (
                  <OptionRow<T>
                    key={getOptionKey(option)}
                    option={option}
                    index={index}
                    listboxId={listboxId}
                    isSelected={selectedKeys.has(getOptionKey(option))}
                    isHighlighted={index === highlight}
                    onHighlight={setHighlight}
                    onToggle={handleToggle}
                    renderOption={renderOption}
                    getOptionLabel={getOptionLabel}
                  />
                ))
              )}
            </ul>
          </RP.Content>
        </RP.Portal>
      </RP.Root>
    </div>
  );
}
